import React, { useCallback } from 'react';
import Activation from '../Index';
import SettingsPopup from 'components/shared/Popups/Settings/Index';
import './index.scss';
import * as Yup from 'yup';
import { Formik } from 'formik';
import RecruiterActivationProfileForm from './RecruiterActivationProfileForm';

interface RecruiterSettingsStepProps {
  t: any;
  onBack: () => void;
  onSubmit: () => void;
  setCompanyData: (data: any) => void;
  companyData: any;
  currentStep: number;
  totalSteps: number;
}

const recruiterSettingsValidation = (t: any, companyData: any) =>
  Yup.object({
    first_name: Yup.string()
      .trim()
      .min(3, `${t('least')} 3 ${t('characters')}`)
      .required(t('required')),
    last_name: Yup.string()
      .trim()
      .min(3, `${t('least')} 3 ${t('characters')}`)
      .required(t('required')),
    job_title: Yup.string()
      .trim()
      .min(2, `${t('least')} 2 ${t('characters')}`)
      .max(50, `${t('less_than')} 50 ${t('characters')}`)
      .required(t('required')),
    ...(companyData && companyData.email
      ? {
          email: Yup.string()
            .trim()
            .email(t('invalid_email'))
            .required(t('required'))
        }
      : {}),
    // Only require password fields if newPassword is present
    ...(companyData && companyData.newPassword
      ? {
          oldPassword: Yup.string().trim().required(t('required')),
          newPassword: Yup.string()
            .trim()
            .min(8, `${t('least')} 8 ${t('characters')}`)
            .required(t('required')),
          repeatPassword: Yup.string()
            .trim()
            .oneOf([Yup.ref('newPassword')], t('Passwords do not match'))
            .required(t('required'))
        }
      : {})
  });

const RecruiterSettingsStep: React.FC<RecruiterSettingsStepProps> = ({
  t,
  onBack,
  onSubmit,
  setCompanyData,
  companyData,
  currentStep,
  totalSteps
}) => {
  // We do not use handleClosePopup, so pass a no-op
  const handleClosePopup = useCallback(() => {}, []);

  return (
    <Activation
      title={t('Recruiter Settings')}
      currentStep={currentStep}
      totalSteps={totalSteps}
    >
      <Formik
        initialValues={companyData || {}}
        enableReinitialize
        validationSchema={recruiterSettingsValidation(t, companyData)}
        onSubmit={(values, actions) => {
          onSubmit();
        }}
      >
        {({
          validateForm,
          errors,
          touched,
          isValid,
          handleSubmit,
          values,
          setFieldValue,
          setFieldTouched
        }) => {
          // Keep companyData in sync
          React.useEffect(() => {
            setCompanyData(values);
          }, [values]);

          const handleNext = async () => {
            const formErrors = await validateForm();
            // Mark all fields with errors as touched
            Object.keys(formErrors).forEach((field) => {
              setFieldTouched(field, true, false);
            });
            if (Object.keys(formErrors).length === 0) {
              handleSubmit();
            }
            // else: errors will be passed to SettingsPopup and shown inline
          };

          return (
            <>
              <div style={{ width: '100%' }}>
                <RecruiterActivationProfileForm
                  values={values}
                  errors={errors}
                  touched={touched}
                  setFieldValue={setFieldValue}
                  isValid={isValid}
                  isSubmitting={false}
                  t={t}
                />
              </div>
              <div className="buttons">
                <button className="btn back" type="button" onClick={onBack}>
                  {t('Back')}
                </button>
                <button className="btn next" type="button" onClick={handleNext}>
                  {t('Next')}
                </button>
              </div>
            </>
          );
        }}
      </Formik>
    </Activation>
  );
};

export default RecruiterSettingsStep;
